import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Composant d'affichage / saisie de notes en étoiles (1-5).
 *
 * Mode lecture : passe `value` + `readOnly`. Affiche les étoiles pleines/vides.
 * Mode saisie  : passe `value` + `onChange`. Clic = sélection, hover = preview.
 *
 * `size` accepte 'sm' (h-3.5), 'md' (h-5, défaut), 'lg' (h-7).
 */
interface Props {
  value: number; // 0..5 (0 = aucune note)
  onChange?: (v: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Texte d'accompagnement à droite (ex: "4,2 · 12 avis") */
  label?: string;
}

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-5 w-5',
  lg: 'h-7 w-7',
};

export function StarRating({
  value,
  onChange,
  readOnly = false,
  size = 'md',
  className,
  label,
}: Props) {
  const sizeClass = SIZE_CLASSES[size];
  const interactive = !readOnly && !!onChange;

  return (
    <div className={cn('inline-flex items-center gap-0.5', className)}>
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= Math.round(value);
        if (interactive) {
          return (
            <button
              key={star}
              type="button"
              onClick={() => onChange?.(star)}
              className="p-0.5 rounded transition-colors hover:bg-[#D4A574]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A574]/40"
              aria-label={`Donner ${star} étoile${star > 1 ? 's' : ''}`}
            >
              <Star
                className={cn(
                  sizeClass,
                  isFilled
                    ? 'fill-[#D4A574] text-[#D4A574]'
                    : 'fill-transparent text-neutral-600'
                )}
              />
            </button>
          );
        }
        return (
          <Star
            key={star}
            className={cn(
              sizeClass,
              isFilled
                ? 'fill-[#D4A574] text-[#D4A574]'
                : 'fill-transparent text-neutral-700'
            )}
          />
        );
      })}
      {label && (
        <span className="ml-1.5 text-xs text-neutral-400 tabular-nums">
          {label}
        </span>
      )}
    </div>
  );
}
